import { computed, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'

import { ApiError } from '@/lib/http/types.js'
import type { PermissionName } from '@/constants/app/permission.constant.js'
import { isTwoFactorRequired, type Credentials, type LoginResponse, type Operator } from '@/types/auth/auth.type.js'
import { useServices } from '@/types/shared/service.type.js'

/**
 * Who is signed in, and what they may see.
 *
 * ## It holds no credential
 *
 * The session is two `httpOnly` cookies the browser manages. This store holds the
 * operator's **identity and permissions**, in memory, and nothing else — no token, no
 * `localStorage`, no persistence plugin. Closing the tab forgets everything here; the
 * cookie is what survives, and `hydrate()` is how the store is rebuilt from it.
 *
 * ## Permissions are a `Set`, deliberately
 *
 * `has()` on a 40-element array is a linear scan run once per rendered control, and the nav
 * alone asks the question ~20 times per navigation. More importantly the backend **already
 * expanded** a super-admin's permissions before sending them, so there is no client-side
 * role expansion here and must never be: a second expansion is a second rule to keep in
 * step with the server's, and the one that wins is the wrong one.
 */
export const useAuthStore = defineStore('auth', () => {
  // Resolved once, at store construction, rather than inside each action: `inject()` is
  // only valid during a setup, and calling it from an action that runs on a click would
  // return `undefined` with a warning nobody reads.
  const services = useServices()

  /**
   * `shallowRef`, not `ref`: the operator is replaced wholesale and never edited in place,
   * so deep reactivity would walk a 40-element array on every assignment for no benefit.
   */
  const operator = shallowRef<Operator | null>(null)

  /** The permission names, as a set. Rebuilt whenever the operator is. */
  const permissions = shallowRef<ReadonlySet<string>>(new Set())

  /**
   * Whether `hydrate()` has finished, successfully or not.
   *
   * The router's guard **must** wait for this. Without it the first navigation runs while
   * `operator` is still `null`, every guarded route redirects to the login screen, and a
   * signed-in operator opening a bookmark lands on sign-in — the single most common bug in
   * a cookie-session SPA.
   */
  const ready = ref(false)

  /** Per-operation, never a shared `isLoading`. §5 of the architecture. */
  const signingIn = ref(false)

  const isAuthenticated = computed(() => operator.value !== null)

  const mustChangePassword = computed(() => operator.value?.must_change_password === true)

  /** The second factor, as `/auth/me` reports it. `required && !enabled` is the banner. */
  const twoFactor = computed(() => operator.value?.two_factor ?? null)

  /**
   * `can` takes a `PermissionName`, so a typo fails the build.
   *
   * Were this `string`, `can('shop-veiw')` would be `false` forever and the control it
   * guards would simply never render — no error, no log, nothing to notice.
   */
  const can = (permission: PermissionName): boolean => permissions.value.has(permission)

  /** All of them, not any. The route guard and the nav both mean "all". */
  const canAll = (required: readonly PermissionName[]): boolean => required.every(can)

  function adopt(next: Operator | null): void {
    operator.value = next
    permissions.value = new Set(next?.permissions ?? [])
  }

  /**
   * A correct password earns a session — or, with an authenticator enrolled, a challenge.
   * Nothing is adopted for a challenge: the operator is not signed in yet, and the screen
   * that follows must not render as if they were.
   */
  async function login(credentials: Credentials): Promise<LoginResponse> {
    signingIn.value = true

    try {
      const outcome = await services.auth.login(credentials)

      if (!isTwoFactorRequired(outcome)) {
        adopt(outcome)
        ready.value = true
      }

      return outcome
    } finally {
      // `finally`, so a 401 leaves the form usable. `:disabled="isSubmitting"` is the only
      // thing gating the submit button, and a flag left `true` on failure deadlocks the
      // one control that could retry.
      signingIn.value = false
    }
  }

  /** The second step. The challenge is a cookie the browser holds; only the code travels. */
  async function completeTwoFactor(code: string): Promise<Operator> {
    signingIn.value = true

    try {
      const signedIn = await services.auth.completeTwoFactor(code)

      adopt(signedIn)
      ready.value = true

      return signedIn
    } finally {
      signingIn.value = false
    }
  }

  /**
   * The hydrate in flight, so two callers share one request.
   *
   * There are always two on a cold boot: `bootstrap.ts` starts one, and the router's
   * `sessionGuard` starts another because `ready` is still `false` while the first is on
   * the wire. `app.use(router)` begins the initial navigation during **install** — not at
   * mount — so the two overlap every single time.
   *
   * The result was two identical `/admin/auth/me` requests on every page load. Harmless
   * looking, and wrong in a way that matters: it doubles the load on the one path every
   * visitor takes, and it is the shape that becomes a thundering herd the moment hydrate
   * does anything expensive. A test that counted the calls found it — and the comment here
   * previously *claimed* the waiting behaviour that this variable now actually implements.
   */
  let inFlight: Promise<void> | null = null

  /**
   * Rebuilds the store from the session cookie.
   *
   * A 401 is the **expected** answer for a visitor who is not signed in, so it resolves
   * rather than throwing: an unauthenticated boot is not an error state, it is the login
   * screen. Anything else rethrows — a 500 here means the backend is broken, and silently
   * showing a login form for it sends the operator to type their password at a service
   * that cannot check it.
   */
  function hydrate(): Promise<void> {
    // Cleared when it settles, so a later hydrate — after `reset()`, after a sign-out —
    // fetches again rather than replaying a stale answer forever.
    inFlight ??= fetchOperator().finally(() => {
      inFlight = null
    })

    return inFlight
  }

  async function fetchOperator(): Promise<void> {
    try {
      adopt(await services.auth.me())
    } catch (error: unknown) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        adopt(null)

        return
      }

      throw error
    } finally {
      ready.value = true
    }
  }

  /**
   * Signs out locally **whatever the request does**.
   *
   * The local state is cleared unconditionally: if the call failed, leaving the operator
   * looking signed in here is the worse of the two outcomes — they clicked sign out, very
   * possibly on a shared machine.
   */
  async function logout(): Promise<void> {
    try {
      await services.auth.logout()
    } finally {
      adopt(null)
    }
  }

  /** A profile edit the server has confirmed. Only the name is editable in place. */
  function adoptName(name: string): void {
    if (operator.value !== null) operator.value = { ...operator.value, name }
  }

  /** Called by the Pinia reset plugin at every session boundary. */
  function reset(): void {
    adopt(null)
    ready.value = false
    signingIn.value = false
    // Dropped as well: a hydrate started before a sign-out would otherwise settle
    // afterwards and re-adopt the operator who just signed out.
    inFlight = null
  }

  return {
    operator,
    permissions,
    ready,
    signingIn,
    isAuthenticated,
    mustChangePassword,
    twoFactor,
    can,
    canAll,
    login,
    completeTwoFactor,
    hydrate,
    logout,
    adoptName,
    reset,
  }
})
